/* Progressive presentation only. Existing Canadian content and destinations remain the source. */
(() => {
  function addGuideTools(root, section, heading, body) {
    const template = root.querySelector('template[data-cmg-guide-tools]');
    if (!template || body.querySelector('.cmg-guide-tools')) return;
    const source = document.createElement('div');
    source.className = 'cmg-guide-copy-source';
    while (body.firstChild) source.append(body.firstChild);
    body.append(source, template.content.cloneNode(true));
    const tools = body.querySelector('.cmg-guide-tools');
    const status = tools.querySelector('[data-cmg-guide-status]');
    const copyButton = tools.querySelector('[data-cmg-copy-guide]');
    const printButton = tools.querySelector('[data-cmg-print-guide]');
    const manualCopy = (text) => {
      const field = document.createElement('textarea');
      field.value = text;
      field.readOnly = true;
      field.setAttribute('aria-hidden', 'true');
      field.tabIndex = -1;
      field.style.cssText = 'position:fixed;left:0;top:0;opacity:0;pointer-events:none';
      document.body.append(field);
      try {
        field.select();
        if (!document.execCommand('copy')) throw new Error('Copy unavailable');
      } finally {
        field.remove();
      }
    };
    copyButton.addEventListener('click', async () => {
      if (copyButton.disabled) return;
      const text = source.innerText.trim();
      status.textContent = '';
      copyButton.disabled = true;
      try {
        if (navigator.clipboard?.writeText) {
          try { await navigator.clipboard.writeText(text); }
          catch { manualCopy(text); }
        } else manualCopy(text);
        status.textContent = tools.dataset.cmgCopied;
      } catch {
        status.textContent = tools.dataset.cmgCopyFailed;
      } finally {
        copyButton.disabled = false;
        if (copyButton.isConnected) copyButton.focus({ preventScroll: true });
      }
    });
    printButton.addEventListener('click', () => {
      if (document.body.hasAttribute('data-cmg-printing')) return;
      status.textContent = '';
      const position = { left: window.scrollX, top: window.scrollY };
      const printView = document.createElement('div');
      printView.className = 'cmg-print-view';
      printView.setAttribute('aria-hidden', 'true');
      const title = document.createElement('h1');
      title.textContent = root.querySelector('h1').textContent;
      const label = document.createElement('h2');
      label.textContent = heading.textContent;
      const content = source.cloneNode(true);
      content.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
      printView.append(title, label, content);
      document.body.append(printView);
      document.body.setAttribute('data-cmg-printing', 'true');
      const media = window.matchMedia('print');
      const cleanup = () => {
        printView.remove();
        document.body.removeAttribute('data-cmg-printing');
        window.removeEventListener('afterprint', cleanup);
        media.removeEventListener('change', mediaChange);
        window.scrollTo({ ...position, behavior: 'instant' });
        if (printButton.isConnected) printButton.focus({ preventScroll: true });
      };
      const mediaChange = (event) => { if (!event.matches) cleanup(); };
      window.addEventListener('afterprint', cleanup, { once: true });
      media.addEventListener('change', mediaChange);
      try { window.print(); }
      catch {
        cleanup();
        status.textContent = tools.dataset.cmgPrintFailed;
      }
    });
  }

  function enhance(root) {
    if (root.dataset.cmgEnhanced) return;
    const guide = root.querySelector('.ca-model-guide');
    const nav = guide?.querySelector('.cmg-jump');
    const sidebar = root.querySelector('[data-cmg-sidebar]');
    const menu = root.querySelector('[data-cmg-topic-menu]');
    if (!guide || !nav || !sidebar || !menu) return;
    root.dataset.cmgEnhanced = 'true';
    const overview = guide.querySelector('.cmg-overview');
    if (overview) root.querySelector('[data-cmg-hero-copy]').append(overview);
    root.querySelector('[data-cmg-topics]').append(nav);
    sidebar.hidden = false;
    const desktop = window.matchMedia('(min-width: 1000px)');
    const syncMenu = () => { menu.open = desktop.matches; };
    syncMenu();
    desktop.addEventListener('change', syncMenu);
    menu.addEventListener('toggle', () => { if (desktop.matches && !menu.open) menu.open = true; });

    ['quick-start', 'programming', 'troubleshooting'].forEach((id) => {
      const section = guide.querySelector(`#${id}`);
      const heading = section?.querySelector(':scope > h2');
      if (!section || !heading || section.querySelector(':scope > .cmg-guide')) return;
      const details = document.createElement('details');
      details.className = 'cmg-guide';
      const summary = document.createElement('summary');
      summary.append(root.querySelector('[data-cmg-guide-control]').content.cloneNode(true));
      if (heading.id) summary.setAttribute('aria-describedby', heading.id);
      details.append(summary);
      const body = document.createElement('div');
      body.className = 'cmg-guide-body';
      while (heading.nextSibling) body.append(heading.nextSibling);
      addGuideTools(root, section, heading, body);
      details.append(body);
      section.append(details);
    });

    const imageTemplates = new Map(Array.from(root.querySelectorAll('template[data-cmg-product-image]'), (template) => [template.dataset.cmgProductImage, template]));
    guide.querySelectorAll('#shop-radio a[href]').forEach((link) => {
      const template = imageTemplates.get(link.getAttribute('href'));
      if (!template || link.classList.contains('cmg-product-card')) return;
      const label = document.createElement('span');
      label.className = 'cmg-product-label';
      while (link.firstChild) label.append(link.firstChild);
      link.append(template.content.cloneNode(true), label);
      link.classList.add('cmg-product-card');
    });

    function revealHash(hash, scroll) {
      if (!hash || hash === '#') return;
      let id;
      try { id = decodeURIComponent(hash.slice(1)); } catch { return; }
      const target = document.getElementById(id);
      if (!target || !root.contains(target)) return;
      let parent = target.parentElement;
      while (parent && parent !== root) {
        if (parent instanceof HTMLDetailsElement) parent.open = true;
        parent = parent.parentElement;
      }
      const procedure = target.matches('.cmg-section') ? target.querySelector(':scope > .cmg-guide') : null;
      if (procedure) procedure.open = true;
      nav.querySelectorAll('a[href]').forEach((link) => {
        if (link.getAttribute('href') === hash) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
      if (scroll) requestAnimationFrame(() => target.scrollIntoView({ block: 'start' }));
    }
    root.addEventListener('click', (event) => {
      const link = event.target.closest('a[href^="#"]');
      if (!link || !root.contains(link) || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (menu.contains(link) && !desktop.matches) menu.open = false;
      revealHash(link.getAttribute('href'), true);
    });
    window.addEventListener('hashchange', () => revealHash(window.location.hash, true));
    revealHash(window.location.hash, true);
  }
  function init(scope) {
    if (scope.matches?.('[data-cmg-presentation]')) enhance(scope);
    scope.querySelectorAll('[data-cmg-presentation]').forEach(enhance);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
